import React, { useContext, useState } from 'react'
import { Image } from 'primereact/image'
import { useLocation } from 'react-router-dom'
import { CommonContext } from '../../component/common/context/commonContext'
import { useMountEffect } from 'primereact/hooks'
import PdaTitle from '../../component/common/PdaTitle'
import { GoodsDetailDialogReq, InventoryGoodsDetail, LocationDetail, LocationsSearch } from '../../types/inventory'
import { Divider } from 'primereact/divider'
import { Button } from 'primereact/button'
import LocationSaveDialog from '../../component/location/LocationSaveDialog'
import LocationChangeCheckDialog from '../../component/location/LocationChangeCheckDialog'
import { getImageUrl } from '../../utils/imageUtils'
import GoodsDetailDialog from '../../component/inventory/GoodsDetailDialog'
import { cloneDeep } from 'lodash'
import { includeComma } from '../../utils/inputText'
import { useTranslation } from 'react-i18next'
import { tokens } from 'locales/tokens'
import { translateLocationType } from '../../utils/translateCodeUtils';

const LocationDetailPage = () => {
    const location = useLocation()
    const { $http, $toast } = useContext(CommonContext)
    const { t } = useTranslation()
    const [locationDetail, setLocationDetail] = useState<LocationDetail>()
    const [goodsList, setGoodsList] = useState<InventoryGoodsDetail[]>()
    const [isGoodsDetailUpdate, setIsGoodsDetailUpdate] = useState<boolean>(false)
    const [goodsDetailVisible, setGoodsDetailVisible] = useState<boolean>(false)
    const [isLocationSaveUpdate, setIsLocationSaveUpdate] = useState<boolean>(false)
    const [locationSaveVisible, setLocationSaveVisible] = useState<boolean>(false)
    const [locationChangeCheckVisible, setLocationChangeCheckVisible] = useState<boolean>(false)
    const [goodsDetailReq, setGoodsDetailReq] = useState<GoodsDetailDialogReq>({
        lctPathNm: '',
        lctSeq: '',
        goodsId: '',
        invnSeq: 0,
        lctTypCd: '',
        locationAlias: '',
    })

    useMountEffect(() => {
        findLocationDetail(location.state.lctSeq)
    })

    const findLocationDetail = async (lctSeq: String) => {
        const { locationDetail, goodsList } = await $http().get(`/inventory/location-detail/${lctSeq}`)
        console.log(locationDetail, goodsList)
        console.log(goodsList)
        if (!locationDetail) {
            $toast({
                title: t(tokens.inventory.location_detail_inquiry),
                text: `${tokens.inventory.location_code}=[${lctSeq}] ${t(tokens.common.no_inquiry_information_found)}`,
                icon: 'success',
            })
            return
        }
        setLocationDetail(locationDetail)
        setGoodsList(goodsList)
    }
    const getFontColor = (lctTypCd: string) => {
        switch (lctTypCd) {
            case 'KP':
                return 'rdms-text-warning'
            case 'PK':
                return 'rdms-text-deepblue'
            case 'IN':
                return 'rdms-text-success'
            case 'RT':
            case 'TK':
                return 'rdms-text-danger'
            case 'BD':
                return 'rdms-text-danger'
            default:
                return ''
        }
        // DB, ET, JB, MV, OT
    }
    const onLocationSaveOpen = (isUpdate: boolean) => {
        setIsLocationSaveUpdate(isUpdate)
        setLocationSaveVisible(true)
    }
    const onLocationSaveHide = (isChange: boolean) => {
        if (isChange) {
            findLocationDetail(location.state.lctSeq)
        }
        setLocationSaveVisible(false)
    }

    const onLocationChangeCheckOpen = () => {
        setLocationChangeCheckVisible(true)
    }
    const onLocationChangeCheckHide = (isCheck: boolean) => {
        if (isCheck) {
            setIsLocationSaveUpdate(true) // 수정모드
            setLocationSaveVisible(true)
        }
        setLocationChangeCheckVisible(false)
    }

    const onGoodsDetailHide = (isChange: boolean) => {
        if (isChange) {
            findLocationDetail(location.state.lctSeq)
        }
        setGoodsDetailVisible(false)
    }

    const onClickGoods = (goods: InventoryGoodsDetail) => {
        if (!locationDetail) return
        if (!goods) return
        setGoodsDetailReq({
            lctSeq: locationDetail.lctSeq,
            lctPathNm:
                locationDetail.areaNm +
                '〉' +
                locationDetail.sectorNm +
                '〉' +
                locationDetail.bayId +
                '〉' +
                locationDetail.rackRowId +
                t(tokens.common.row) +
                locationDetail.rackColId +
                t(tokens.common.layer),
            goodsId: goods.goodsId,
            invnSeq: goods.invnSeq,
            lctTypCd: locationDetail.lctTypCd,
            locationAlias: locationDetail.locationAlias,
        })
        setGoodsDetailVisible(true)
    }

    return (
        <>
            <PdaTitle title={t(tokens.inventory.location_detail)} historyBack goToHome />
            {locationDetail && (
                <>
                    <div className="card grid m-3 p-2">
                        <div className="col-12 flex grid">
                            <div className="col-9">
                                <div className="font-bold">
                                    {t(tokens.inventory.location_code)}(
                                    <span className="font-bold rdms-text-deepblue">{t(tokens.inventory.label_code)}</span>)
                                     <span className="font-bold"> / {t(tokens.inventory.location_alias)}</span>
                                </div>
                                <div className="text-sm mt-1">
                                    {locationDetail.lctNo}(<span className="rdms-text-deepblue">{locationDetail.lctSeq}</span>)
                                    <span className="font-bold"> / {locationDetail.locationAlias}</span>
                                </div>
                            </div>
                            <div className="col-3 flex justify-content-center align-items-center p-0">
                                <div className={`w-full text-right font-bold text-2xl ${getFontColor(locationDetail.lctTypCd)}`}>
                                    {translateLocationType(locationDetail.lctTypCd, t)}
                                </div>
                            </div>
                        </div>
                        <Divider className="my-0" />
                        <div className="col-12 flex rdms-text-deepblue font-bold text-lg mt-2">{t(tokens.common.location_)}</div>
                        <div className="col-12 flex text-lg mt-0 pt-0">
                            {`${locationDetail.sectorNm + '' + locationDetail.bayId?.substring(1)}-${locationDetail.rackRowId?.padStart(
                                2,
                                '0',
                            )}-${locationDetail.rackColId?.padStart(2, '0')}`}
                        </div>
                        <div className="col-12 flex rdms-text-deepblue font-bold text-lg mt-2">
                            {t(tokens.inventory.placement_item_count)} / {t(tokens.inventory.placement_rate)}
                        </div>
                        <div className="col-12 flex text-lg mt-0 pt-0">
                            <span className="rdms-text-danger mr-1 font-bold">{locationDetail.goodsIdCnt}</span> SKU
                            <span className="mr-3 ml-3">/</span> {includeComma(locationDetail.strgRate, '-')} %
                        </div>
                        <div className="col-12 flex rdms-text-deepblue font-bold text-lg mt-2">{t(tokens.common.last_modified_datetime)}</div>
                        <div className="col-12 flex text-lg mt-0 pt-0">{locationDetail.updDtFm}</div>
                        <div className="col-12 px-3 Location-button">
                            <Button
                                label={t(tokens.inventory.edit_location_information)}
                                size="large"
                                className="w-full p-button-warning"
                                onClick={() => onLocationChangeCheckOpen()}
                            />
                        </div>
                    </div>

                    <div className="grid m-3 mb-0 pl-2">
                        <div className="col-12 flex grid">
                            <div className="font-bold">{t(tokens.inventory.inventory_info)}</div>
                        </div>
                    </div>
                    <div className="grid m-3 mt-0 p-2">
                        <div className="m-0 p-0 col-12 align-self-center">
                            {goodsList &&
                                goodsList.map((goods) => (
                                    <div className="card grid px-1 py-1 mb-3" onClick={() => onClickGoods(goods)}>
                                        <div className="col-4 align-self-center p-0 pl-1">
                                            <Image src={getImageUrl(goods.imageUri)} width="90" height="110" />
                                        </div>
                                        <div className="col-8">
                                            <div className="col-12 flex p-0">
                                                <div className="col-12 text-left p-0 rdms-text-deepblue font-bold">
                                                    {t(tokens.common.product_name)} | {t(tokens.common.product_barcode)}
                                                </div>
                                            </div>
                                            <div className="col-12 flex p-0 mt-1 pb-2">
                                                <div className="col-12 text-left text-sm p-0">{goods.goodsNm} | {goods.sysCd}</div>
                                            </div>
                                            <div className="col-12 flex p-0 mt-2">
                                                <div className="col-12 text-left p-0 rdms-text-deepblue font-bold">
                                                    {t(tokens.common.specification)} | {t(tokens.common.unit)} | {t(tokens.common.quantity)}
                                                </div>
                                            </div>
                                            <div className="col-12 flex p-0 mt-1 pb-2">
                                                <div className="col-12 text-left text-sm p-0">
                                                    {goods.specTxt} | {goods.gdslsUnitCd} | {goods.goodsQty}
                                                </div>
                                            </div>
                                            <div className="col-12 flex p-0 mt-2">
                                                {/*<div className="col-4 text-left p-0 rdms-text-deepblue font-bold">적치기간</div>*/}
                                                <div className="col-12 text-left p-0 rdms-text-deepblue font-bold">
                                                    {t(tokens.common.expiration_date)} | {t(tokens.common.lot_no)}
                                                </div>
                                            </div>
                                            <div className="col-12 flex p-0 mt-1">
                                                {/*<div className="col-4 text-left text-sm p-0">{goods.strgDay}일</div>*/}
                                                <div className="col-12 text-left text-sm p-0">
                                                    {goods.shlfYmdFm} (
                                                    {goods.shlfRemainDay <= 0
                                                        ? 'D+' + includeComma(-goods.shlfRemainDay)
                                                        : 'D-' + includeComma(goods.shlfRemainDay)}
                                                    ) | {goods.lotNo}
                                                </div>
                                            </div>
                                        </div>
                                        {/*<div className="col-9">*/}
                                        {/*    <div className="col-12 flex p-0">*/}
                                        {/*        <div className="col-12 flex p-0">*/}
                                        {/*            <div className="col-5 text-left p-0 rdms-text-deepblue font-bold">적치기간</div>*/}
                                        {/*            <div className="col-7 text-left p-0 rdms-text-deepblue font-bold">{t(tokens.common.expiration_date)}</div>*/}
                                        {/*        </div>*/}
                                        {/*        <div className="col-12 flex p-0">*/}
                                        {/*            <div className="col-5 text-left text-sm p-0">{goods.strgDay}일</div>*/}
                                        {/*            <div className="col-7 text-left text-sm p-0">*/}
                                        {/*                {goods.shlfYmdFm} (D-{goods.shlfRemainDay})*/}
                                        {/*            </div>*/}
                                        {/*        </div>*/}
                                        {/*    </div>*/}
                                        {/*</div>*/}
                                    </div>
                                    // <div className="flex mt-1 grid" key={goods.rowNum} onClick={() => onClickGoods(goods)}>
                                    //     <div className="col-2 flex justify-content-center align-items-center">
                                    //         <div className={`w-full text-left  text-4xl`}>
                                    //             <Image src={getImageUrl(goods.imageUri)} width="50" />
                                    //         </div>
                                    //     </div>
                                    //     <div className="col-10 flex flex-wrap p-0 grid">
                                    //         <div className="col-12 flex flex-wrap">
                                    //             <div className="col-12 flex p-0">
                                    //                 <div className="col-6 text-left text-sm p-0">{t(tokens.common.product_name)}</div>
                                    //                 <div className="col-6 text-left text-sm p-0 ">{t(tokens.common.specification)} | {t(tokens.common.unit)} | {t(tokens.common.quantity)}</div>
                                    //             </div>
                                    //             <div className="col-12 flex p-0">
                                    //                 <div className="col-6 text-left text-sm p-0">{goods.goodsNm}</div>
                                    //                 <div className="col-6 text-left text-sm p-0">
                                    //                     {goods.specTxt} | {goods.gdslsUnitCd} | {goods.goodsQty}
                                    //                 </div>
                                    //             </div>
                                    //         </div>
                                    //         <div className="col-12 flex flex-wrap">
                                    //             <div className="col-12 flex p-0">
                                    //                 <div className="col-6 text-left text-sm p-0">적치기간</div>
                                    //                 <div className="col-6 text-left text-sm p-0 ">{t(tokens.common.expiration_date)}</div>
                                    //             </div>
                                    //             <div className="col-12 flex p-0">
                                    //                 <div className="col-6 text-left text-sm p-0">{goods.strgDay}일</div>
                                    //                 <div className="col-6 text-left text-sm p-0">
                                    //                     {goods.shlfYmdFm} (D-{goods.shlfRemainDay})
                                    //                 </div>
                                    //             </div>
                                    //         </div>
                                    //     </div>
                                    // </div>
                                ))}
                        </div>
                    </div>
                    {locationSaveVisible && (
                        <LocationSaveDialog
                            isUpdate={isLocationSaveUpdate}
                            visible={locationSaveVisible}
                            onHide={(e) => onLocationSaveHide(e)}
                            data={locationDetail}
                        />
                    )}
                    {locationChangeCheckVisible && (
                        <LocationChangeCheckDialog
                            // isUpdate={isLocationSaveUpdate}
                            visible={locationChangeCheckVisible}
                            onHide={(e) => onLocationChangeCheckHide(e)}
                        />
                    )}
                    {goodsDetailVisible && (
                        <GoodsDetailDialog
                            isUpdate={isGoodsDetailUpdate}
                            visible={goodsDetailVisible}
                            onHide={(e) => onGoodsDetailHide(e)}
                            data={goodsDetailReq}
                        />
                    )}
                </>
            )}
        </>
    )
}

export default LocationDetailPage
